import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Footer } from '../components/Footer';
const SHOPIFY_APP_URL = 'https://apps.shopify.com/corkship-eparcel-shipping';
const SECTIONS = [{
  id: 'prerequisites',
  label: 'Before You Start'
}, {
  id: 'installation',
  label: 'Installation'
}, {
  id: 'credentials',
  label: 'Australia Post Credentials'
}, {
  id: 'sender-address',
  label: 'Sender Address'
}, {
  id: 'parcel-sizes',
  label: 'Parcel Sizes'
}, {
  id: 'creating-shipments',
  label: 'Creating Shipments'
}, {
  id: 'labels',
  label: 'Printing Labels'
}, {
  id: 'manifests',
  label: 'Manifests'
}, {
  id: 'postcode-validation',
  label: 'Postcode Validation'
}, {
  id: 'troubleshooting',
  label: 'Troubleshooting'
}];
function StepBadge({
  n
}: {
  n: number;
}) {
  return <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#0284C7] text-white text-sm font-bold flex-shrink-0" data-id="element-532">{n}</span>;
}
function Callout({
  type,
  children
}: {
  type: 'info' | 'warning' | 'tip';
  children: React.ReactNode;
}) {
  const styles = {
    info: {
      bg: 'bg-[#EFF6FF]',
      border: 'border-[#0284C7]/30',
      icon: '💡',
      text: 'text-[#0369A1]'
    },
    warning: {
      bg: 'bg-[#FFFBEB]',
      border: 'border-[#F59E0B]/30',
      icon: '⚠️',
      text: 'text-[#92400E]'
    },
    tip: {
      bg: 'bg-[#F0FDF4]',
      border: 'border-[#10B981]/30',
      icon: '✓',
      text: 'text-[#065F46]'
    }
  }[type];
  return <div className={`${styles.bg} border ${styles.border} rounded-xl p-4 flex gap-3 my-5`} data-id="element-533">
      <span className="text-lg leading-tight flex-shrink-0" data-id="element-534">{styles.icon}</span>
      <p className={`text-sm leading-relaxed ${styles.text} m-0`} data-id="element-535">{children}</p>
    </div>;
}
function SectionHeading({
  id,
  children
}: {
  id: string;
  children: React.ReactNode;
}) {
  return <h2 id={id} className="font-heading text-2xl font-bold text-[#0F172A] mt-12 mb-6 pb-3 border-b border-[#E2E8F0] scroll-mt-24" data-id="element-536">{children}</h2>;
}
function SubHeading({
  children
}: {
  children: React.ReactNode;
}) {
  return <h3 className="font-heading text-lg font-bold text-[#0F172A] mt-8 mb-3" data-id="element-537">{children}</h3>;
}
function Step({
  n,
  title,
  children
}: {
  n: number;
  title: string;
  children: React.ReactNode;
}) {
  return <div className="flex gap-4 mb-6" data-id="element-538">
      <StepBadge n={n} data-id="element-539" />
      <div className="flex-1" data-id="element-540">
        <p className="font-semibold text-[#0F172A] text-[15px] mb-1" data-id="element-541">{title}</p>
        <div className="text-[#475569] text-[15px] leading-relaxed" data-id="element-542">{children}</div>
      </div>
    </div>;
}
export function DocsShopifyPage() {
  const [activeSection, setActiveSection] = useState('prerequisites');
  useEffect(() => {
    const handleScroll = () => {
      for (const section of [...SECTIONS].reverse()) {
        const el = document.getElementById(section.id);
        if (el && el.getBoundingClientRect().top <= 120) {
          setActiveSection(section.id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <div className="min-h-screen flex flex-col bg-[#F1F5F9]" data-id="element-543">

      {/* Top Nav */}
      <header className="fixed top-0 left-0 right-0 z-[1000] bg-[#F1F5F9]/90 backdrop-blur-xl border-b border-[#0F172A]/[0.08]" data-id="element-544">
        <div className="flex items-center justify-between py-4 max-w-[1200px] mx-auto px-6" data-id="element-545">
          <Link to="/" className="flex items-center gap-2 font-heading text-xl font-bold text-[#0F172A] no-underline" data-id="element-546">
            <img src="https://assets.corkship.io/CorkShip%20Logo%20Original%20png.png" alt="CorkShip" className="w-8 h-8 object-contain" data-id="element-547" />
            CorkShip
          </Link>
          <div className="hidden md:inline-flex items-center gap-2 text-sm text-[#64748B]" data-id="element-548">
            <span className="inline-flex items-center gap-1.5 bg-[#5E8E3E]/10 text-[#5E8E3E] px-3 py-1 rounded-full text-xs font-semibold" data-id="element-549">
              <svg viewBox="0 0 24 24" fill="#5E8E3E" className="w-3.5 h-3.5" data-id="element-550"><path d="M15.337 3.415c-.157-.018-.314.064-.392.203-.078.14-.048.317.073.427.744.683 1.18 1.595 1.18 2.455 0 1.903-1.627 3.5-3.5 3.5s-3.5-1.597-3.5-3.5c0-.86.436-1.772 1.18-2.455.12-.11.15-.287.072-.427-.078-.14-.235-.22-.392-.203C6.996 3.7 4.5 6.585 4.5 10c0 4.136 3.364 7.5 7.5 7.5s7.5-3.364 7.5-7.5c0-3.415-2.496-6.3-4.163-6.585z" data-id="element-551" /></svg>
              Shopify
            </span>
            Documentation
          </div>
        </div>
      </header>

      {/* Docs Hero */}
      <div className="pt-[80px] bg-[#0F172A] text-white" data-id="element-552">
        <div className="max-w-[1200px] mx-auto px-6 py-12" data-id="element-553">
          <div className="flex items-center gap-2 text-sm text-white/50 mb-4" data-id="element-554">
            <Link to="/" className="text-white/50 hover:text-white no-underline transition-colors" data-id="element-555">CorkShip</Link>
            <span data-id="element-556">/</span>
            <Link to="/docs" className="text-white/50 hover:text-white no-underline transition-colors" data-id="element-557">Documentation</Link>
            <span data-id="element-558">/</span><span className="text-white" data-id="element-559">Shopify</span>
          </div>
          <div className="flex items-start justify-between gap-8 flex-wrap" data-id="element-560">
            <div data-id="element-561">
              <div className="inline-flex items-center gap-2 bg-[#5E8E3E]/30 text-[#86EFAC] px-3 py-1.5 rounded-full text-xs font-semibold mb-4" data-id="element-562">
                <svg viewBox="0 0 24 24" fill="#86EFAC" className="w-3.5 h-3.5" data-id="element-563"><path d="M15.337 3.415c-.157-.018-.314.064-.392.203-.078.14-.048.317.073.427.744.683 1.18 1.595 1.18 2.455 0 1.903-1.627 3.5-3.5 3.5s-3.5-1.597-3.5-3.5c0-.86.436-1.772 1.18-2.455.12-.11.15-.287.072-.427-.078-.14-.235-.22-.392-.203C6.996 3.7 4.5 6.585 4.5 10c0 4.136 3.364 7.5 7.5 7.5s7.5-3.364 7.5-7.5c0-3.415-2.496-6.3-4.163-6.585z" data-id="element-564" /></svg>
                Shopify Integration
              </div>
              <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-565">Getting Started with CorkShip</h1>
              <p className="text-white/70 text-lg max-w-[560px] leading-relaxed" data-id="element-566">Connect CorkShip to your Shopify store and start creating Australia Post eParcel shipments directly from your admin.</p>
            </div>
            <div className="flex flex-col gap-3 text-sm" data-id="element-567">
              <div className="flex items-center gap-2 text-white/60" data-id="element-568">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4 flex-shrink-0" data-id="element-569"><circle cx="12" cy="12" r="10" data-id="element-570" /><path d="M12 6v6l4 2" data-id="element-571" /></svg>
                ~5 min setup
              </div>
              <div className="flex items-center gap-2 text-white/60" data-id="element-572">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4 flex-shrink-0" data-id="element-573"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" data-id="element-574" /></svg>
                Australia Post account required
              </div>
              <div className="flex items-center gap-2 text-white/60" data-id="element-575">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4 flex-shrink-0" data-id="element-576"><rect x="2" y="7" width="20" height="14" rx="2" data-id="element-577" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" data-id="element-578" /></svg>
                Shopify admin access required
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Layout */}
      <div className="flex-1 max-w-[1200px] mx-auto px-6 py-10 w-full" data-id="element-579">
        <div className="flex gap-10 items-start" data-id="element-580">

          {/* Sidebar */}
          <aside className="hidden lg:block w-56 flex-shrink-0 sticky top-24" data-id="element-581">
            <p className="text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-3" data-id="element-582">On This Page</p>
            <nav className="flex flex-col gap-0.5" data-id="element-583">
              {SECTIONS.map(s => <a key={s.id} href={`#${s.id}`} className={`text-sm px-3 py-2 rounded-lg no-underline transition-colors ${activeSection === s.id ? 'bg-[#0284C7]/10 text-[#0284C7] font-semibold' : 'text-[#64748B] hover:text-[#0F172A] hover:bg-white'}`} data-id="element-584">
                  {s.label}
                </a>)}
            </nav>
            <div className="mt-6 p-4 bg-white rounded-xl border border-[#E2E8F0]" data-id="element-585">
              <p className="text-xs font-semibold text-[#0F172A] mb-1" data-id="element-586">Also see</p>
              <Link to="/docs/auspost-credentials" className="text-xs text-[#0284C7] no-underline hover:underline block" data-id="element-587">Australia Post credentials guide →</Link>
            </div>
            <div className="mt-3 p-4 bg-white rounded-xl border border-[#E2E8F0]" data-id="element-588">
              <p className="text-xs font-semibold text-[#0F172A] mb-2" data-id="element-589">Need help?</p>
              <a href="mailto:support@corkship.io" className="text-xs text-[#0284C7] no-underline hover:underline" data-id="element-590">support@corkship.io</a>
            </div>
          </aside>

          {/* Content */}
          <main className="flex-1 min-w-0" data-id="element-591">
            <div className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] p-8 md:p-10" data-id="element-592">

              <SectionHeading id="prerequisites" data-id="element-593">Before You Start</SectionHeading>
              <p className="text-[#475569] leading-relaxed mb-4" data-id="element-594">Before installing CorkShip you'll need the following ready:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-595">
                {[{
                icon: '📦',
                title: 'Australia Post eParcel Account',
                desc: 'An active eParcel contract with Australia Post. CorkShip is a bring-your-own-account integration — your existing rates apply.'
              }, {
                icon: '🔑',
                title: 'eParcel API Credentials',
                desc: 'Your API Key, API Secret, and Account Number from the Australia Post Developer Centre. New to this? See our credentials guide.'
              }, {
                icon: '🛒',
                title: 'Shopify Admin Access',
                desc: 'Full admin access to your Shopify store to install the app and manage orders.'
              }, {
                icon: '📍',
                title: 'Sender Address',
                desc: "Your winery's dispatch address — this becomes the return address on all shipping labels."
              }].map(item => <div key={item.title} className="flex gap-3 p-4 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]" data-id="element-596">
                    <span className="text-2xl flex-shrink-0" data-id="element-597">{item.icon}</span>
                    <div data-id="element-598">
                      <p className="font-semibold text-[#0F172A] text-sm mb-1" data-id="element-599">{item.title}</p>
                      <p className="text-[#64748B] text-sm leading-relaxed" data-id="element-600">{item.desc}</p>
                    </div>
                  </div>)}
              </div>
              <Callout type="info" data-id="element-601">
                Don't have your API credentials yet? Follow our step-by-step <Link to="/docs/auspost-credentials" className="font-semibold text-[#0284C7] no-underline hover:underline" data-id="element-602">Australia Post credentials guide</Link> to get them in about 10 minutes.
              </Callout>

              <SectionHeading id="installation" data-id="element-603">Installation</SectionHeading>
              <p className="text-[#475569] leading-relaxed mb-6" data-id="element-604">CorkShip installs directly from the Shopify App Store.</p>
              <Step n={1} title="Install from the Shopify App Store" data-id="element-605">Visit the <a href={SHOPIFY_APP_URL} target="_blank" rel="noopener noreferrer" className="text-[#0284C7] font-semibold hover:underline" data-id="element-606">CorkShip listing on the Shopify App Store</a> and click <strong data-id="element-607">Install</strong>. You'll be redirected to your Shopify admin to confirm the required permissions.</Step>
              <Step n={2} title="Approve the permissions" data-id="element-608">CorkShip requests access to orders, shipping, and fulfilment data. Review and approve to complete the installation.</Step>
              <Step n={3} title="Open CorkShip from your Shopify admin" data-id="element-609">After installation, CorkShip appears under <strong data-id="element-610">Apps</strong> in your Shopify sidebar. Click it to open and follow the setup steps below.</Step>
              <Callout type="tip" data-id="element-611">CorkShip runs entirely inside your Shopify admin — you never need to leave to create shipments or print labels.</Callout>

              <SectionHeading id="credentials" data-id="element-612">Australia Post Credentials</SectionHeading>
              <p className="text-[#475569] leading-relaxed mb-4" data-id="element-613">CorkShip connects directly to your Australia Post eParcel account. Your credentials are stored securely and never shared.</p>
              <Callout type="info" data-id="element-614">
                Need help finding your credentials? Our <Link to="/docs/auspost-credentials" className="font-semibold text-[#0284C7] no-underline hover:underline" data-id="element-615">Australia Post credentials guide</Link> walks through every step.
              </Callout>
              <Step n={1} title="Go to Settings in CorkShip" data-id="element-616">Open CorkShip from your Shopify apps and navigate to <strong data-id="element-617">Settings → Australia Post</strong>.</Step>
              <Step n={2} title="Enter your three credentials" data-id="element-618">Paste in your <strong data-id="element-619">API Key</strong>, <strong data-id="element-620">API Secret</strong>, and <strong data-id="element-621">Account Number</strong> (10 digits, zero-padded).</Step>
              <Step n={3} title="Toggle Production Mode on" data-id="element-622">Switch <strong data-id="element-623">Production Mode</strong> on before creating real shipments. Leave it off while testing.</Step>
              <Step n={4} title="Save" data-id="element-624">Click Save. CorkShip will test the connection — a green confirmation means you're connected and ready to ship.</Step>
              <Callout type="warning" data-id="element-625">Keep your API credentials confidential. Do not share them via email or store them in plain text. CorkShip encrypts them at rest.</Callout>

              <SectionHeading id="sender-address" data-id="element-626">Sender Address</SectionHeading>
              <p className="text-[#475569] leading-relaxed mb-4" data-id="element-627">Your sender address is printed on every shipping label as the return address and is used by Australia Post to determine eligible shipping routes.</p>
              <Step n={1} title="Go to Settings → Sender Address" data-id="element-628">Fill in your winery's full dispatch address — business name, street address, suburb, state, and postcode.</Step>
              <Step n={2} title="Ensure the postcode matches your eParcel contract" data-id="element-629">Australia Post routes shipments based on the sender postcode. If your contract postcode doesn't match your sender address, shipments may be rejected.</Step>
              <Callout type="info" data-id="element-630">If you dispatch from multiple locations, contact us at support@corkship.io — multi-location sender support is on the roadmap.</Callout>

              <SectionHeading id="parcel-sizes" data-id="element-631">Parcel Sizes</SectionHeading>
              <p className="text-[#475569] leading-relaxed mb-4" data-id="element-632">CorkShip automatically assigns the right carton to each order based on bottle count. Configure your parcel sizes in <strong data-id="element-633">Settings → Parcel Sizes</strong> to match your actual carton dimensions and packaging weights.</p>
              <div className="overflow-x-auto mb-6" data-id="element-634">
                <table className="w-full text-sm border-collapse" data-id="element-635">
                  <thead data-id="element-636">
                    <tr className="bg-[#F8FAFC]" data-id="element-637">
                      <th className="text-left px-4 py-3 text-[#0F172A] font-semibold border-b border-[#E2E8F0]" data-id="element-638">Configuration</th>
                      <th className="text-left px-4 py-3 text-[#0F172A] font-semibold border-b border-[#E2E8F0]" data-id="element-639">Bottles</th>
                      <th className="text-left px-4 py-3 text-[#0F172A] font-semibold border-b border-[#E2E8F0]" data-id="element-640">Typical use</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-641">
                    {[{
                    config: '1-pack',
                    bottles: '1',
                    use: 'Single bottle gift orders'
                  }, {
                    config: '2-pack',
                    bottles: '2',
                    use: 'Small orders, gift pairs'
                  }, {
                    config: '3-pack',
                    bottles: '3',
                    use: 'Mixed three-packs'
                  }, {
                    config: '6-pack',
                    bottles: '6',
                    use: 'Half-case orders, wine clubs'
                  }, {
                    config: '12-pack',
                    bottles: '12',
                    use: 'Full case, wine club dispatches'
                  }].map((row, i) => <tr key={row.config} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F8FAFC]'} data-id="element-642">
                        <td className="px-4 py-3 font-medium text-[#0F172A] border-b border-[#E2E8F0]" data-id="element-643">{row.config}</td>
                        <td className="px-4 py-3 text-[#475569] border-b border-[#E2E8F0]" data-id="element-644">{row.bottles}</td>
                        <td className="px-4 py-3 text-[#475569] border-b border-[#E2E8F0]" data-id="element-645">{row.use}</td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
              <Callout type="tip" data-id="element-646">Always enter your <strong data-id="element-647">packaging weight</strong> (the weight of the empty carton) separately from bottle weight. CorkShip adds these together to calculate the total parcel weight sent to Australia Post.</Callout>

              <SectionHeading id="creating-shipments" data-id="element-648">Creating Shipments</SectionHeading>
              <p className="text-[#475569] leading-relaxed mb-4" data-id="element-649">Shipments are created from the CorkShip Shipments view, which shows all paid, unfulfilled Shopify orders ready to ship.</p>
              <Step n={1} title="Open the Shipments tab in CorkShip" data-id="element-650">CorkShip loads your pending orders from Shopify and validates each address against Australia Post.</Step>
              <Step n={2} title="Review the order" data-id="element-651">Check the order details, bottle count, and destination address. Orders with address warnings are highlighted — fix these in Shopify before creating a shipment.</Step>
              <Step n={3} title="Click Create Shipment" data-id="element-652">CorkShip automatically assigns bottles to the optimal carton configuration and submits the shipment to Australia Post. The tracking number appears within seconds.</Step>
              <Callout type="warning" data-id="element-653">Orders shipping to <strong data-id="element-654">Victoria (VIC)</strong> automatically include Identity on Delivery as required by Liquor Control Victoria legislation. The recipient will need to show ID at the door.</Callout>

              <SectionHeading id="labels" data-id="element-655">Printing Labels</SectionHeading>
              <p className="text-[#475569] leading-relaxed mb-4" data-id="element-656">Once a shipment has been created, generate the Australia Post shipping label as a PDF.</p>
              <Step n={1} title="Select the order in the Shipments list" data-id="element-657">Orders with a shipment show a <strong data-id="element-658">Generate Labels</strong> button.</Step>
              <Step n={2} title="Click Generate Labels" data-id="element-659">CorkShip fetches the label PDF from Australia Post and opens it in your browser for printing or saving.</Step>
              <Step n={3} title="Attach the label to your parcel" data-id="element-660">Print on A4 or thermal label stock. Attach securely to the outside of the carton with the barcode clearly visible.</Step>
              <Callout type="info" data-id="element-661">If an order spans multiple parcels, CorkShip generates a combined PDF with one label per parcel in a single download.</Callout>

              <SectionHeading id="manifests" data-id="element-662">Manifests</SectionHeading>
              <p className="text-[#475569] leading-relaxed mb-4" data-id="element-663">A manifest groups your shipments for Australia Post pickup or drop-off. You must close a manifest before handing parcels over.</p>
              <Step n={1} title="Open the Manifests tab in CorkShip" data-id="element-664">You'll see your current open manifest and any past closed manifests.</Step>
              <Step n={2} title="Review included shipments" data-id="element-665">Shipments with labels printed are automatically added to the open manifest.</Step>
              <Step n={3} title="Close the manifest" data-id="element-666">Click <strong data-id="element-667">Close Manifest</strong> when ready to hand over to Australia Post. This lodges the manifest and locks it.</Step>
              <Step n={4} title="Download the Order Summary PDF" data-id="element-668">After closing, download the manifest summary PDF to hand to the driver or keep for your records.</Step>
              <Callout type="warning" data-id="element-669">Always close the manifest before your Australia Post driver arrives. Parcels in an open manifest will not be scanned into the network correctly.</Callout>

              <SectionHeading id="postcode-validation" data-id="element-670">Postcode Validation</SectionHeading>
              <p className="text-[#475569] leading-relaxed mb-4" data-id="element-671">CorkShip includes a Shopify checkout validation extension that blocks orders to postcodes you've restricted — useful for liquor licence compliance or excluding remote areas where wine cannot be shipped.</p>
              <SubHeading data-id="element-672">Activating postcode validation</SubHeading>
              <Step n={1} title="Go to Settings → Postcode Validation in CorkShip" data-id="element-673">Configure your allowed or blocked postcode rules — by individual postcode, prefix, or state.</Step>
              <Step n={2} title="Activate the checkout rule in Shopify" data-id="element-674">Navigate to your Shopify admin → <strong data-id="element-675">Settings → Checkout → Checkout rules</strong> and activate the CorkShip postcode validation rule.</Step>
              <Callout type="info" data-id="element-676">Postcode validation only fires at checkout — it does not affect orders created manually in the Shopify admin.</Callout>

              <SectionHeading id="troubleshooting" data-id="element-677">Troubleshooting</SectionHeading>
              {[{
              q: 'Shipment creation failed with an address error',
              a: "The suburb, state, and postcode don't match the Australia Post database. Correct the delivery address in Shopify and try again."
            }, {
              q: 'My credentials are being rejected',
              a: "Double-check your API Key, API Secret, and Account Number — no extra spaces, and the Account Number must be exactly 10 digits with leading zeros. Ensure Production Mode matches your credentials (sandbox keys only work with it off)."
            }, {
              q: "Labels aren't generating",
              a: 'Labels can only be generated after a shipment has been successfully created. If the shipment shows an error, delete it and create it again after fixing the underlying issue.'
            }, {
              q: "The manifest won't close",
              a: 'Ensure all shipments have labels generated. Australia Post requires labels before a manifest can be lodged.'
            }, {
              q: 'An order is missing from the CorkShip view',
              a: 'CorkShip only shows orders that are paid and unfulfilled with a shipping delivery method. Check the order status in Shopify.'
            }, {
              q: 'Postcode validation is not firing at checkout',
              a: 'Ensure the CorkShip checkout rule is activated in Shopify admin → Settings → Checkout → Checkout rules. It must be manually enabled after installation.'
            }].map(item => <div key={item.q} className="mb-5 border border-[#E2E8F0] rounded-xl overflow-hidden" data-id="element-678">
                  <div className="flex items-start gap-3 p-4 bg-[#F8FAFC]" data-id="element-679">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4 text-[#0284C7] flex-shrink-0 mt-0.5" data-id="element-680"><circle cx="12" cy="12" r="10" data-id="element-681" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" data-id="element-682" /><line x1="12" y1="17" x2="12.01" y2="17" data-id="element-683" /></svg>
                    <p className="font-semibold text-[#0F172A] text-sm" data-id="element-684">{item.q}</p>
                  </div>
                  <div className="p-4" data-id="element-685">
                    <p className="text-[#475569] text-sm leading-relaxed" data-id="element-686">{item.a}</p>
                  </div>
                </div>)}

              <div className="mt-10 p-6 bg-[#0F172A] rounded-2xl text-center" data-id="element-687">
                <p className="font-heading text-lg font-bold text-white mb-2" data-id="element-688">Still stuck?</p>
                <p className="text-white/60 text-sm mb-4" data-id="element-689">We're happy to help. Reach out and we'll get back to you within one business day.</p>
                <a href="mailto:support@corkship.io" className="inline-flex items-center gap-2 bg-[#0284C7] text-white px-6 py-2.5 rounded-lg text-sm font-semibold no-underline hover:bg-[#0369A1] transition-colors" data-id="element-690">Email Support</a>
              </div>

            </div>
          </main>
        </div>
      </div>

      <Footer data-id="element-691" />
    </div>;
}